import { ContentMetadata, SelectedMedia } from '@/lib/model';
import { ImageProps } from 'next/image';
import { VideoPlayer } from '@/app/components/pages/home';
import { cn } from '@/lib/utils';
import { ImageBox } from '@/app/components/cards/common/ImageBox';
import { LinkWithChannelLink } from '@/app/components/link/LinkWithChannel';
import { ContentRouter } from '../../link/ContentRouter';

type Props = Partial<ImageProps> & {
  item: SelectedMedia;
  playVideo?: boolean;
  contentMetadata: ContentMetadata;
  linkClassName?: string;
  playerClassName?: string;
  viewInDetailPage?: boolean;
  autoplay?: boolean;
  hidePlaceholder?: boolean;
  isLite?: boolean;
  noLink?: boolean;
  href?: string;
  withDefaultControlBar?: boolean;
  isHovered?: boolean;
  imageClassName?: string;
  Placeholder?: React.ReactNode;
  playerAspectRatio?: boolean;
};
export const VideoImageBox = ({
  item,
  playVideo = true,
  linkClassName,
  contentMetadata,
  autoplay,
  playerClassName,
  viewInDetailPage,
  hidePlaceholder,
  isLite,
  noLink,
  withDefaultControlBar,
  isHovered,
  imageClassName,
  Placeholder,
  playerAspectRatio,
  ...props
}: Props) => {
  const isVideo = item?.media?.type === 'VIDEO';
  const isAudio = item?.media?.type === 'AUDIO';

  if (!item) return null;
  if (noLink) {
    return (
      <>
        {(isVideo || isAudio) && playVideo ? (
          <VideoPlayer
            contentMetadata={contentMetadata}
            media={item}
            isVideo={isVideo}
            isAudio={isAudio}
            autoplay={autoplay}
            playerClassName={playerClassName}
            viewInDetailPage={viewInDetailPage}
            isLite={isLite}
            hidePlaceholder={hidePlaceholder}
            withDefaultControlBar={withDefaultControlBar}
            isHovered={isHovered}
            Placeholder={Placeholder}
            playerAspectRatio={playerAspectRatio}
            {...props}
          />
        ) : (
          <div className={cn(linkClassName, 'relative')}>
            <ImageBox item={item} {...props} imageClassName={imageClassName} />
          </div>
        )}
      </>
    );
  }

  if (viewInDetailPage && contentMetadata.slug) {
    return (
      <>
        {isVideo || isAudio ? (
          <ContentRouter
            desktopContentMetadata={contentMetadata}
            mobileContentMetadata={contentMetadata}
            subType={contentMetadata?.subType}
          >
            <VideoPlayer
              contentMetadata={contentMetadata}
              media={item}
              isVideo={isVideo}
              isAudio={isAudio}
              autoplay={autoplay}
              playerClassName={playerClassName}
              viewInDetailPage={viewInDetailPage}
              isLite={isLite}
              hidePlaceholder={hidePlaceholder}
              withDefaultControlBar={withDefaultControlBar}
              isHovered={isHovered}
              Placeholder={Placeholder}
              playerAspectRatio={playerAspectRatio}
              {...props}
            />
          </ContentRouter>
        ) : (
          <ContentRouter
            desktopContentMetadata={contentMetadata}
            mobileContentMetadata={contentMetadata}
            className={cn(linkClassName, 'relative')}
          >
            <ImageBox item={item} {...props} imageClassName={imageClassName} />
          </ContentRouter>
        )}
      </>
    );
  }

  return (
    <>
      {(isVideo || isAudio) && playVideo ? (
        <VideoPlayer
          contentMetadata={contentMetadata}
          media={item}
          isVideo={isVideo}
          isAudio={isAudio}
          autoplay={autoplay}
          playerClassName={playerClassName}
          viewInDetailPage={viewInDetailPage}
          isLite={isLite}
          hidePlaceholder={hidePlaceholder}
          withDefaultControlBar={withDefaultControlBar}
          isHovered={isHovered}
          Placeholder={Placeholder}
          playerAspectRatio={playerAspectRatio}
          {...props}
        />
      ) : props.href ? (
        <LinkWithChannelLink
          href={props.href ?? ''}
          className={cn(linkClassName, 'relative')}
        >
          <ImageBox item={item} {...props} imageClassName={imageClassName} />
        </LinkWithChannelLink>
      ) : (
        <div className={cn(linkClassName, 'relative')}>
          <ImageBox item={item} {...props} imageClassName={imageClassName} />
        </div>
      )}
    </>
  );
};
